import type { CSSProperties } from 'react';
import { useToast } from '../../../../hooks/useToast';
import styles from '../ScenarioGenerator.module.css';
import type { SceneItemProps } from '../SceneItem';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { useGenerationState } from '../../state/useGenerationState';
import { useMaterialLookup } from './useMaterialLookup';

type BrandCutMaterialThumbProps = Pick<SceneItemProps, 'scene' | 'sceneKey'> & {
  /**
   * 'compact' = ストーリーボードのシーン詳細欄 (幅160px・縦長は高さ160px基準)。
   * 'fill' = フレーム列 (親の幅いっぱいにシナリオのアスペクト比で表示)。
   */
  size: 'compact' | 'fill';
};

/**
 * ブランドカット (タイトル/エンドカード) に紐付いた素材のサムネイル。動画素材なら再生アイコンを
 * 重ね、静止画素材ならその画像を出す。クリックすると素材モーダルが開く (動画は再生される)。
 */
export function BrandCutMaterialThumb({ scene, sceneKey: key, size }: BrandCutMaterialThumbProps) {
  const { setPreviewModalMaterial, fetchMaterialById } = useSceneHandlers();
  const { sceneVideoMaterialId } = useSceneStateContext();
  const { findLoadedMaterial } = useMaterialLookup();
  const { videoSettings } = useGenerationState();
  const toast = useToast();

  const boundVideoId = sceneVideoMaterialId[key] || scene.video_material_id;
  const materialId = boundVideoId || scene.image_material_id;
  const material = findLoadedMaterial(materialId);
  const thumbUrl =
    material?.thumbnail_url
    || material?.thumbnailUrl
    || scene.image_url
    || null;
  // サムネイルの枠はシナリオのアスペクト比に合わせる (CSS の 16/9 は
  // 比率が読めないときの既定)。compact で縦長 (9:16 等) は幅 160px だと縦に
  // 伸び過ぎるため、高さ 160px を基準にして幅を比率から計算する
  // (display:block の要素は width:auto だと親幅いっぱいに広がり、
  // aspect-ratio から幅が導かれないため明示的に渡す)。
  const [arW, arH] = String(videoSettings.aspectRatio || '').split(':').map(Number);
  const hasRatio = Number.isFinite(arW) && Number.isFinite(arH) && arW > 0 && arH > 0;
  const thumbStyle: CSSProperties = {
    ...(hasRatio ? { aspectRatio: `${arW} / ${arH}` } : {}),
    ...(size === 'fill'
      ? { width: '100%' }
      : (hasRatio && arH > arW ? { width: Math.round(160 * arW / arH), height: 160 } : {})),
  };

  return (
    <button
      type="button"
      className={styles.brandCutVideoThumb}
      style={thumbStyle}
      onClick={async () => {
        if (!materialId) return;
        if (material) {
          setPreviewModalMaterial(material);
          return;
        }
        const fetched = await fetchMaterialById(materialId);
        if (fetched) {
          setPreviewModalMaterial(fetched);
        } else {
          toast.error(boundVideoId ? '動画の読み込みに失敗しました' : '画像の読み込みに失敗しました');
        }
      }}
      title={boundVideoId ? '素材モーダルを開いて再生' : '素材モーダルを開く'}
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt={boundVideoId ? 'ブランドカットの動画素材' : 'ブランドカットの画像素材'} />
      ) : (
        <span className={styles.brandCutVideoThumbEmpty}>{boundVideoId ? '動画素材' : '画像素材'}</span>
      )}
      {boundVideoId && (
        <span className={styles.brandCutVideoThumbPlay} aria-hidden="true">
          <img src="/button_play_green.svg" alt="" />
        </span>
      )}
    </button>
  );
}
